/**
 * BOLO page — papers the office is waiting on. The clerk or supervisor enters who asked and what to
 * look for; when a matching paper is keyed in (or approved from ScanAI) it is marked urgent and linked.
 */
import { useState } from 'react';
import { api } from '../shared/api.ts';
import { useAuth } from '../shared/auth.tsx';
import { Link } from '../shared/router.tsx';
import { Badge, Card, Empty, ErrorBox, Field, Notice, Spinner, useAction, useLoad } from '../shared/ui.tsx';

const blank = { requestedBy: '', paperTypeCode: '', plaintiff: '', defendantName: '', addressLine1: '', city: '', zip: '', phone: '', caseNumber: '', note: '' };
const when = (d: string) => new Date(d).toLocaleString('en-US', { month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' });

export function BoloPage() {
  const { can } = useAuth();
  const list = useLoad(() => api.get<any>('/api/bolos'), []);
  const meta = useLoad(() => api.get<any>('/api/meta'), []);
  const [f, setF] = useState(blank);
  const [found, setFound] = useState<{ boloId: string; items: any[] } | null>(null);
  const [saved, setSaved] = useState(false);
  const a = useAction();
  const set = (k: keyof typeof blank) => (e: { target: { value: string } }) => (setSaved(false), setF({ ...f, [k]: e.target.value }));
  const manage = can('bolo.manage');

  if (list.error) return <ErrorBox error={list.error} />;
  if (!list.data || !meta.data) return <Spinner />;
  const all = list.data.bolos as any[];
  const active = all.filter((b) => b.status === 'active');
  const matched = all.filter((b) => b.status === 'matched');
  const cancelled = all.filter((b) => b.status === 'cancelled');
  const typeName = (c: string | null) => (c ? meta.data.paperTypes.find((t: any) => t.code === c)?.name ?? c : 'Any paper');
  const unsaved = Object.values(f).some(Boolean);

  const save = () =>
    a.run(async () => {
      const body = Object.fromEntries(Object.entries(f).map(([k, v]) => [k, v.trim() || undefined]));
      const r = await api.post<any>('/api/bolos', body);
      setF(blank);
      setSaved(true);
      setFound(r.alreadyHere.length ? { boloId: r.id, items: r.alreadyHere } : null);
      list.reload();
    });
  const check = (id: string) =>
    a.run(async () => {
      const r = await api.get<any>(`/api/bolos/${id}/candidates`);
      setFound({ boloId: id, items: r.alreadyHere });
    });
  const linkIt = (boloId: string, paperId: string) =>
    a.run(async () => {
      await api.post(`/api/bolos/${boloId}/link`, { paperId });
      setFound(null);
      list.reload();
    });
  const cancel = (id: string) => {
    const reason = window.prompt('Why is this BOLO being cancelled?');
    if (!reason) return;
    a.run(async () => {
      await api.post(`/api/bolos/${id}/cancel`, { reason });
      list.reload();
    });
  };

  const who = (b: any) => (
    <>
      <strong>{b.defendant_name || '—'}</strong>
      {b.address_line1 && <div className="small">{[b.address_line1, b.city, b.zip].filter(Boolean).join(', ')}</div>}
      {b.plaintiff && <div className="small muted">Plaintiff: {b.plaintiff}</div>}
      {b.case_number && <div className="small muted">Case {b.case_number}</div>}
    </>
  );

  return (
    <div className="stack">
      <div>
        <h1>BOLO — papers we're waiting on</h1>
        <p className="muted">
          When the Sheriff or a magistrate says "serve this the day it comes in", put it here. When a paper with the same defendant, address or case number is keyed in or
          approved from ScanAI, it's marked <strong>URGENT</strong> automatically and the clerk sees who asked for it.
        </p>
      </div>

      {manage && (
        <Card title="New BOLO">
          <form
            data-unsaved={unsaved ? 'true' : undefined}
            onSubmit={(e) => {
              e.preventDefault();
              save();
            }}
          >
            <div className="grid2">
              <Field label="Requested by" hint="Sheriff, Chief Deputy, Magistrate Smith…">
                <input value={f.requestedBy} onChange={set('requestedBy')} />
              </Field>
              <Field label="Paper type">
                <select value={f.paperTypeCode} onChange={set('paperTypeCode')}>
                  <option value="">Any paper</option>
                  {meta.data.paperTypes.map((t: any) => (
                    <option key={t.code} value={t.code}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Plaintiff">
                <input value={f.plaintiff} onChange={set('plaintiff')} />
              </Field>
              <Field label="Defendant name">
                <input value={f.defendantName} onChange={set('defendantName')} />
              </Field>
              <Field label="Address">
                <input value={f.addressLine1} onChange={set('addressLine1')} placeholder="123 Main St" />
              </Field>
              <Field label="City">
                <input value={f.city} onChange={set('city')} />
              </Field>
              <Field label="Zip">
                <input value={f.zip} onChange={set('zip')} inputMode="numeric" />
              </Field>
              <Field label="Phone number">
                <input value={f.phone} onChange={set('phone')} inputMode="tel" />
              </Field>
              <Field label="Case number" hint="If you know it">
                <input value={f.caseNumber} onChange={set('caseNumber')} />
              </Field>
            </div>
            <Field label="Note" hint="What should happen when it comes in">
              <textarea rows={2} value={f.note} onChange={set('note')} placeholder="Serve same day. Call the Sheriff when served." />
            </Field>
            <p className="muted small">Needs at least a defendant name, an address or a case number. Plaintiff alone won't trigger a match — landlords file too many papers.</p>
            <div className="row gap">
              <button className="btn primary" disabled={a.busy || !(f.defendantName.trim() || f.addressLine1.trim() || f.caseNumber.trim())}>
                {a.busy ? 'Saving…' : 'Save BOLO'}
              </button>
              {saved && !found && <span className="ok-text">Saved. The system will watch for it.</span>}
            </div>
          </form>
        </Card>
      )}

      <ErrorBox error={a.error} />
      {found && (
        <Notice tone={found.items.length ? 'warn' : 'info'}>
          {found.items.length ? (
            <>
              <strong>This paper may already be here.</strong> Is it one of these?
              <ul className="plain">
                {found.items.map((p) => (
                  <li key={p.paperId} className="row gap between">
                    <span>
                      <Link to={`/papers/${p.paperId}`}>{p.civilosNumber}</Link> — {p.defendant} <span className="muted small">(same {p.matchedOn.filter((m: string) => m !== 'plaintiff').join(', ')})</span>
                    </span>
                    {manage && (
                      <button className="btn sm warn" onClick={() => linkIt(found.boloId, p.paperId)} disabled={a.busy}>
                        That's it — mark urgent
                      </button>
                    )}
                  </li>
                ))}
              </ul>
              <button className="btn sm ghost" onClick={() => setFound(null)}>
                None of these — keep watching
              </button>
            </>
          ) : (
            <>
              Nothing on hand matches yet. Still watching.{' '}
              <button className="btn sm ghost" onClick={() => setFound(null)}>
                OK
              </button>
            </>
          )}
        </Notice>
      )}

      <Card title={`Waiting for (${active.length})`}>
        {active.length === 0 ? (
          <Empty>No BOLOs. Nothing we're waiting on.</Empty>
        ) : (
          <div className="table-wrap">
            <table className="table bolo-table">
              <thead>
                <tr>
                  <th>Looking for</th>
                  <th>Paper</th>
                  <th>Requested by</th>
                  <th>Phone</th>
                  <th>Note</th>
                  <th>Entered</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {active.map((b) => (
                  <tr key={b.id}>
                    <td>{who(b)}</td>
                    <td>{typeName(b.paper_type_code)}</td>
                    <td>{b.requested_by || <span className="muted">—</span>}</td>
                    <td>{b.phone || <span className="muted">—</span>}</td>
                    <td className="small">{b.note}</td>
                    <td className="small muted">
                      {when(b.created_at)}
                      <br />
                      {b.created_by_name}
                    </td>
                    <td className="nowrap">
                      <button className="btn sm" onClick={() => check(b.id)} disabled={a.busy}>
                        Already here?
                      </button>{' '}
                      {manage && (
                        <button className="btn sm ghost" onClick={() => cancel(b.id)} disabled={a.busy}>
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title={`Found (${matched.length})`}>
        {matched.length === 0 ? (
          <Empty>None found yet.</Empty>
        ) : (
          <div className="table-wrap">
            <table className="table bolo-table">
              <thead>
                <tr>
                  <th>Looking for</th>
                  <th>Requested by</th>
                  <th>Paper</th>
                  <th>Found</th>
                </tr>
              </thead>
              <tbody>
                {matched.map((b) => (
                  <tr key={b.id}>
                    <td>{who(b)}</td>
                    <td>{b.requested_by || <span className="muted">—</span>}</td>
                    <td>
                      {b.matches.map((m: any) => (
                        <div key={m.paperId}>
                          <Link to={`/papers/${m.paperId}`}>{m.civilosNumber}</Link> <Badge tone={m.status === 'SERVED' ? 'ok' : 'warn'}>{m.status.replace(/_/g, ' ').toLowerCase()}</Badge>
                          <div className="small muted">same {m.matchedOn.join(', ')}</div>
                        </div>
                      ))}
                    </td>
                    <td className="small muted">{b.matched_at ? when(b.matched_at) : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {cancelled.length > 0 && (
        <details>
          <summary className="muted">Cancelled ({cancelled.length})</summary>
          <ul className="plain small">
            {cancelled.map((b) => (
              <li key={b.id}>
                {b.defendant_name || b.address_line1 || b.case_number} — {b.cancel_reason} <span className="muted">({b.cancelled_by_name}, {when(b.cancelled_at)})</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

/** Big banner shown after intake / ScanAI approval and on the paper screen. */
export function BoloBanner({ hits }: { hits: { requestedBy?: string | null; requested_by?: string | null; note: string | null; phone: string | null; civilosNumber?: string }[] }) {
  if (!hits?.length) return null;
  return (
    <div className="bolo-banner" role="alert">
      <div className="bolo-title">BOLO — this paper was being watched for. Marked URGENT.</div>
      {hits.map((h, i) => (
        <div key={i} className="bolo-line">
          {h.civilosNumber && <strong>{h.civilosNumber}: </strong>}
          Requested by <strong>{h.requestedBy ?? h.requested_by ?? 'office'}</strong>
          {h.note && <> — {h.note}</>}
          {h.phone && <> · Phone {h.phone}</>}
        </div>
      ))}
    </div>
  );
}
